import { useMemo, useState } from 'react'
import axios from 'axios'
import { AlertCircle, Check, Clock, Loader2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { API_BASE_URL, getMe, getStoredBearerToken } from '../lib/api'
import { businessTimeZone } from '../lib/businessTime'

/**
 * The business's own clock (BRIEF-E7). One line: "Times here are on <zone>
 * time". Sign-up set it from the owner's browser; the owner or an admin can
 * change it here (PUT /auth/v2/business-time-zone, their own business only),
 * and the pages read it again from /auth/me straight after. A plain member
 * sees the line and no control; the backend refuses them with 403 anyway.
 */

// Used only where the browser cannot list its zones.
const FALLBACK_ZONES = [
  'Pacific/Auckland',
  'Pacific/Chatham',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Perth',
  'Australia/Hobart',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
]

/** Every IANA zone the browser knows, with the current one always on it. */
export function zoneChoices(current: string): string[] {
  const list = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf?.('timeZone')
  const zones = list && list.length ? [...list] : [...FALLBACK_ZONES]
  if (!zones.includes(current)) zones.unshift(current)
  return zones
}

export async function putBusinessTimeZone(timeZone: string): Promise<string> {
  const token = getStoredBearerToken()
  const response = await axios.put<{ business_time_zone: string }>(
    `${API_BASE_URL}/auth/v2/business-time-zone`,
    { time_zone: timeZone },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      timeout: 30000,
    },
  )
  return response.data.business_time_zone
}

export function BusinessClockSection() {
  const principal = useAuthStore((s) => s.principal)
  const zone = principal?.business_time_zone || businessTimeZone()
  const canChange =
    principal?.role === 'client' &&
    (principal.org_role === 'owner' || principal.org_role === 'admin')
  const choices = useMemo(() => zoneChoices(zone), [zone])
  const [choice, setChoice] = useState(zone)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  if (principal?.role !== 'client') return null

  async function save() {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      await putBusinessTimeZone(choice)
      // The pages take the clock from /auth/me: read it again now.
      useAuthStore.setState({ principal: await getMe() })
      setSaved(true)
    } catch {
      setError('That did not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      aria-labelledby="business-clock-heading"
      className="mt-6 rounded-2xl border border-gray-200 bg-white p-5"
    >
      <h2
        id="business-clock-heading"
        className="flex items-center gap-2 text-sm font-semibold text-navy"
      >
        <Clock className="w-4 h-4 text-brass" aria-hidden="true" />
        Your business's clock
      </h2>
      <p className="mt-2 text-sm text-gray-700">Times here are on {zone} time</p>
      {canChange && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label htmlFor="business-clock-zone" className="text-xs text-gray-500">
            Time zone
          </label>
          <select
            id="business-clock-zone"
            value={choice}
            onChange={(e) => {
              setChoice(e.target.value)
              setSaved(false)
            }}
            className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
          >
            {choices.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || choice === zone}
            className="tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-3 py-1.5 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50 transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            Save time zone
          </button>
          <span aria-live="polite">
            {saved && (
              <span className="inline-flex items-center gap-1 text-xs text-success">
                <Check className="w-3.5 h-3.5" aria-hidden="true" />
                Saved
              </span>
            )}
          </span>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 flex items-center gap-1.5 text-sm text-danger">
          <AlertCircle className="w-4 h-4" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  )
}
